import {
  Stack,
  Divider,
  Skeleton,
  Flex,
  Center,
  Button,
} from '@chakra-ui/react'
import { SentryErrorComponent } from '@components/Fallback'
import { FieldGroup } from '@components/Forms/Layout/FieldGroup'
import { CenteredSpinner } from '@components/Loader/CenteredSpinner'
import { ConfirmDeleteModal } from '@components/Modals/ConfirmDeleteModal'
import { FailedToLoad } from '@components/Placeholders/FailedToLoad'
import {
  useStaffListUsersQuery,
  useStaffRevokeUserSessionsMutation,
  useStaffResetUserPasswordMutation,
} from '@features/staffMember/api/user'
import { StaffUpdateUserDetails } from '@features/staffMember/components/users/StaffUpdateUserDetails'
import { StaffUpdateUserEmail } from '@features/staffMember/components/users/StaffUpdateUserEmail'
import useCmxToast from '@hooks/useCmxToast'
import { captureException } from '@sentry/react'
import { createFileRoute, useParams } from '@tanstack/react-router'

const revokeSessionsButton = (onOpen) => (
  <Button colorScheme={'red'} onClick={onOpen}>
    Revoke Sessions
  </Button>
)

const ManageUser = ({ userId }) => {
  const toast = useCmxToast()
  const { user, loading, failed, fetching } = useStaffListUsersQuery(null, {
    selectFromResult: ({ data, isLoading, isFetching, isError }) => ({
      user: data?.find((u) => u.id === userId),
      loading: isLoading,
      fetching: isFetching,
      failed: isError,
    }),
  })
  const [revokeSessions] = useStaffRevokeUserSessionsMutation()
  const [sendResetPasswordEmail, { isLoading: resettingPassword }] =
    useStaffResetUserPasswordMutation()

  const handleRevokeSessions = async () => {
    if (!user) return
    try {
      await revokeSessions(user.id).unwrap()
      toast({ status: 'success', title: 'User sessions revoked' })
    } catch (error) {
      captureException(error)
      toast({ status: 'error', title: 'Unable to revoke user sessions' })
    }
  }

  const handleResetPassword = async () => {
    if (!user) return
    try {
      await sendResetPasswordEmail(user.id).unwrap()
      toast({ status: 'success', title: 'Reset Password Email Sent' })
    } catch (error) {
      captureException(error)
      toast({ status: 'error', title: 'Unable to send reset password email' })
    }
  }

  return (
    <Stack spacing={6} divider={<Divider />} maxW={'5xl'} mx={'auto'}>
      <Skeleton isLoaded={!loading}>
        {user && (
          <FieldGroup title={'Details'} description={'Update the user details'}>
            <StaffUpdateUserDetails user={user} />
          </FieldGroup>
        )}
        {fetching && <CenteredSpinner />}
        {failed && <FailedToLoad />}
      </Skeleton>
      {user && (
        <FieldGroup title={'Email'} description={'Update the user email'}>
          <StaffUpdateUserEmail
            userId={userId}
            defaultEmail={user?.emailAddress}
          />
        </FieldGroup>
      )}
      {user && (
        <FieldGroup
          title={'Reset Password'}
          description={'Send a reset password email to the user'}
        >
          <Flex justify={'space-around'} w={'full'}>
            <Center>
              <Button
                colorScheme={'blue'}
                onClick={handleResetPassword}
                isLoading={resettingPassword}
                loadingText={'Sending reset email'}
              >
                Send reset password email
              </Button>
            </Center>
          </Flex>
        </FieldGroup>
      )}
      {user && (
        <FieldGroup
          title={'Revoke Sessions'}
          description={'Log the user out of all sessions'}
        >
          <Flex justify={'space-around'} w={'full'}>
            <Center>
              <ConfirmDeleteModal
                title={'Confirm Session Revocation'}
                message={
                  'Are you sure you want to revoke all sessions for this user?'
                }
                onConfirm={handleRevokeSessions}
                button={revokeSessionsButton}
              />
            </Center>
          </Flex>
        </FieldGroup>
      )}
    </Stack>
  )
}

const UserDetailRoute = () => {
  const { userId } = useParams({ from: '/_auth/staff/u/$userId' })

  return <ManageUser userId={userId} />
}

export const Route = createFileRoute('/_auth/staff/u/$userId')({
  component: UserDetailRoute,
  beforeLoad: () => ({ getTitle: () => 'User Details' }),
  errorComponent: SentryErrorComponent,
  loader: () => ({
    crumb: 'User Details',
  }),
})
